import { ValidationMessage, ValidationProps } from 'ui';
import { AnyAction } from 'redux';
import { STATE } from 'app/constants';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching } from 'app/types-shared';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { complexValidationFromServer, ServerValidationErrors } from 'app/infrastructure/data/validation';
import { downloadFile } from 'app/infrastructure/platform/file';
import { ErrorNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import { CONTENT_DISPOSITION_HEADER, createUploader } from 'app/infrastructure/remote-data';
import * as navigate from 'app/area/shared/navigation';
import { isForbiddenStatus, blockAccess, isNotFoundStatus } from 'app/area/shared/actions-utils';
import { actionCreators as defaultFiltersActionCreators } from 'app/area/shared/default-filters/actions';
import { api as defaultFiltersApi } from 'app/area/shared/default-filters/api';
import { mapFilterMetadata } from 'app/area/shared/default-filters/utils/map-filter-metadata';
import { mapFiltersFromWire } from 'app/area/shared/default-filters/utils/map-filters';
import { mapFilterValidation } from 'app/area/shared/default-filters/utils/map-filter-validation';
import { validateDefaultFilters } from 'app/area/shared/default-filters/utils/validate-default-filters';
import { DefaultFilter } from 'app/area/shared/default-filters/types-store';
import { actionCreators as commonActionCreators } from 'app/area/shared/saved-reports/actions';
import { api as commonApi } from 'app/area/shared/saved-reports/api';
import { mapSavedReportsWarnings } from 'app/area/shared/saved-reports/utils/map-saved-reports-warnings';
import { mapSavedReportsValidation } from 'app/area/shared/saved-reports/utils/map-saved-reports-validation';
import { mapCompanyReportFromWire, mapCompanyReportToWire, mapTemplateFromWire } from './utils/map-company-report';
import { companyReportAdminTranslation, CompanyReportAdminTranslation } from '../constants';
import { mapSavedReportsFromWire } from './utils/map-saved-reports';
import { normalizeSequence } from './utils/normalize-sequence';
import {
  RequestLanguageOptions, ReceiveLanguageOptions, FailLanguageOptions,
  RequestCompanyReport, ReceiveCompanyReport, FailCompanyReport,
  RequestDeleteCompanyReport, ReceiveDeleteCompanyReport, FailDeleteCompanyReport,
  RequestSaveCompanyReport, ReceiveSaveCompanyReport, FailSaveCompanyReport,
  RequestTemplate, ReceiveTemplate, FailTemplate,
  UpdateTextDescription, UpdateItem, ChangeLanguage, CopyLanguageToAll,
  ClearForm, UpdateValidation, ResetValidation,
  UpdateTextItem, UpdateTextItemDescription, RemoveText, RemoveAllTexts, AddText, ResetAllTextsValidation,
  ResetTextValidation, UpdateTextsValidation,
  UpdateMetadataItem, UpdateMetadataValidation, RemoveMetadata, RemoveAllMetadata, AddMetadata, ResetMetadataValidation,
  ResetAllMetadataValidation,
  ResetTextsValidation
} from './action-types';
import { api } from './api';
import { CompanyReportForm, defaultFilterCompanyReportConfig } from './constants';
import { endpoints, saveReportsEndpoints } from './endpoints';
import { getBackRoute } from './route';
import { CompanyReportItem, TextValue, CompanyReportItemValidation, MetadataValue, SavedReportCompanyReport } from './types-store';
import { mapTextsErrors } from 'app/area/company-reports-administration/form/utils/map-texts-errors';

export const actionCreators = {

  ...commonActionCreators<SavedReportCompanyReport>(CompanyReportForm, saveReportsEndpoints, normalizeSequence),
  ...defaultFiltersActionCreators(CompanyReportForm, endpoints),

  loadLanguageOptions: (): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestLanguageOptions());

      try {

        const languageOptions = await api.loadLanguageOptions();

        dispatch(new ReceiveLanguageOptions(
          languageOptions.map(option => ({
            value: option.v,
            description: option.d
          }))
        ));
      } catch (err) {

        if (err && (err.userMessages || err.message)) {
          dispatch(new ErrorNotification(err.userMessages || err.message));
        }
        dispatch(new FailLanguageOptions());
      }
    },

  createCompanyReport: (): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestCompanyReport(undefined));

      try {

        const [rootUserGroups, filterDomains] = await Promise.all([
          api.loadRootUserGroups(),
          defaultFiltersApi.loadFilterDomains(endpoints)
        ]);

        const filtersMetadata = filterDomains?.filterDomain?.length > 0
          ? await defaultFiltersApi.loadFiltersMetadata(endpoints, filterDomains.filterDomain.map(filter => filter.domain))
          : [];

        dispatch(new ReceiveCompanyReport(undefined, mapCompanyReportFromWire({
          subset: rootUserGroups?.rootCodes
        }), [], [], filtersMetadata?.map(mapFilterMetadata) ?? []));

      } catch (err) {

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.companyReportAdministration,
            translationId: companyReportAdminTranslation.no_access_to_company_report_admin
          });
        } else {

          const errorMessage = err && (err.userMessages || err.message);
          !!errorMessage && dispatch(new ErrorNotification(errorMessage));

          navigate.to({ pathname: getBackRoute() });
        }

        dispatch(new FailCompanyReport(undefined));
      }
    },

  loadCompanyReport: (id: number, translation: CompanyReportAdminTranslation): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestCompanyReport(id));

      try {

        const companyReport = await api.loadCompanyReport(id);

        const evaluationsWireRequest = commonApi.loadSavedReportEvaluations(saveReportsEndpoints,
          companyReport?.savedReport?.map(report => report.code?.v));

        const filtersMetadataRequest = companyReport?.filter?.length > 0
          ? defaultFiltersApi.loadFiltersMetadata(endpoints, companyReport?.filter?.map(filter => filter.domain?.v))
          : [];

        const [evaluationsWire, filtersMetadata] = await Promise.all([evaluationsWireRequest, filtersMetadataRequest]);

        const companyReportItem = mapCompanyReportFromWire(companyReport);
        const defaultFilters = mapFiltersFromWire(companyReport?.filter, filtersMetadata, defaultFilterCompanyReportConfig);

        dispatch(new ReceiveCompanyReport(id,
          companyReportItem,
          mapSavedReportsFromWire(companyReport?.savedReport, evaluationsWire),
          defaultFilters,
          filtersMetadata.map(mapFilterMetadata)
        ));

        const textsErrors = mapTextsErrors(
          companyReportItem.textSet?.value,
          companyReportItem.textId,
          companyReportItem.textDescription,
          companyReportItem.texts, translation.no_text);

        if (Object.keys(textsErrors).length > 0) {
          dispatch(new UpdateValidation(textsErrors));
        }

        const evaluationErrors = mapSavedReportsWarnings(evaluationsWire);

        if (Object.keys(evaluationErrors).length > 0) {
          actionCreators.updateSavedReportValidation(evaluationErrors)(dispatch, getState);
        }

        const defaultFiltersErrors = validateDefaultFilters(defaultFilters, translation);

        if (Object.keys(defaultFiltersErrors).length > 0) {
          actionCreators.updateFiltersValidation(defaultFiltersErrors)(dispatch, getState);
        }

      } catch (err) {

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.companyReportAdministration,
            translationId: companyReportAdminTranslation.no_access_to_company_report_admin
          });
        } else {

          const errorMessage = err && (err.userMessages || err.message);
          !!errorMessage && dispatch(new ErrorNotification(errorMessage));

          navigate.to({ pathname: getBackRoute() });
        }

        dispatch(new FailCompanyReport(id));
      }
    },

  deleteCompanyReport: (id: number, successMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestDeleteCompanyReport(id));

      try {
        await api.deleteCompanyReport(id);

        navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });

        dispatch(new ReceiveDeleteCompanyReport(id));
        dispatch(new SuccessNotification(successMessage));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailDeleteCompanyReport(id));

        if (isNotFoundStatus(err)) {
          navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
        }
      }
    },

  saveCompanyReport: (
    companyReport: CompanyReportItem,
    savedReports: SavedReportCompanyReport[],
    filters: DefaultFilter[],
    successMessage: string,
    validationErrorMessage: string
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      const id = companyReport?.id;

      dispatch(new RequestSaveCompanyReport(id));

      try {

        const companyReportWire = mapCompanyReportToWire(companyReport, savedReports, filters);

        if (isDefinedIncludingZero(id)) {
          await api.saveCompanyReport(id, companyReportWire);

          dispatch(new ReceiveSaveCompanyReport(id, undefined));
        } else {
          const newCompanyReportId = await api.createCompanyReport(companyReportWire);

          if (savedReports?.length > 0) {

            dispatch(new ReceiveSaveCompanyReport(id, undefined));

            navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
          } else {
            dispatch(new ReceiveSaveCompanyReport(id, newCompanyReportId));
          }
        }

        navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
        dispatch(new SuccessNotification(successMessage));

      } catch (err) {

        if (err?.userMessages?.length ?? 0 > 0) {
          dispatch(new ErrorNotification(err.userMessages));
        }

        if (Object.keys(err?.validationErrors ?? {}).length > 0) {
          dispatch(new ErrorNotification(validationErrorMessage));

          const { savedReport: code, filter, texts, ...validation } = err.validationErrors;

          dispatch(new UpdateValidation(complexValidationFromServer(validation)));

          actionCreators.updateTextsValidation(texts, companyReport.texts)(dispatch, getState);
          actionCreators.updateSavedReportValidation(mapSavedReportsValidation({ code }))(dispatch, getState);
          actionCreators.updateFiltersValidation(mapFilterValidation(filter))(dispatch, getState);
        }

        dispatch(new FailSaveCompanyReport(id));

        if (isNotFoundStatus(err)) {
          navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
        }
      }
    },

  updateTextDescription: (id: number, textSet: number): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {
      if (!isDefinedIncludingZero(id) || !isDefinedIncludingZero(textSet)) {
        dispatch(new UpdateTextDescription(undefined));
        return;
      }

      try {

        const text = await api.loadTextDescription(id, textSet);
        dispatch(new UpdateTextDescription(text?.d));

      } catch (err) {
        dispatch(new UpdateTextDescription(undefined));
        dispatch(new UpdateValidation(complexValidationFromServer(err?.validationErrors)));
      }
    },

  updateTemplate: (
    reportId: number,
    file: File,
    translation: CompanyReportAdminTranslation,
    hasUploadRights: boolean = true,
    callback: () => void
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch: any, getState: any) => {
      if (!hasUploadRights) {
        dispatch(new ErrorNotification(translation.no_upload_permission));
        return;
      }

      dispatch(new RequestTemplate(reportId));
      try {
        const xhr = await createUploader(endpoints.specialReportTemplate(reportId), 'PUT');
        const data = new FormData();
        data.append('TemplatePackage', file, file.name);

        xhr.onload = () => {
          if (xhr.status === 200) {
            callback();

            const templateData = JSON.parse(xhr.response)?.data;
            dispatch(new ReceiveTemplate(mapTemplateFromWire(templateData)));
            const successMessage = translation.upload_success;
            dispatch(new SuccessNotification(successMessage));
          } else {
            dispatch(new FailTemplate(reportId));
            dispatch(new ErrorNotification(xhr.response && JSON.parse(xhr.response || '')?.messages?.userMessages?.[0]?.message
              || translation.problem_uploading_file));
          }
        };

        xhr.onerror = () => {
          dispatch(new FailTemplate(reportId));
          dispatch(new ErrorNotification(translation.reload_file));
        };

        xhr.send(data);
      }
      catch (err) {
        dispatch(new FailTemplate(reportId));
        dispatch(new ErrorNotification(err.errorMessage));
      }
    },

  downloadTemplate: (reportId: number | string, fileName: string, translation: CompanyReportAdminTranslation,
    hasDownloadRights: boolean = true) =>
    async (dispatch: any, getState: any) => {
      if (!hasDownloadRights) {
        dispatch(new ErrorNotification(translation.no_download_permission));
        return;
      }
      try {
        const resource = await api.downloadTemplate(reportId);
        const response = await resource.blob();
        const blobName = response?.headers?.get(CONTENT_DISPOSITION_HEADER)?.split('filename=')[1] || fileName;
        downloadFile(response.blob, blobName);
      } catch (err) {
        const errorMessage = translation.file_not_found;
        dispatch(new ErrorNotification(errorMessage));
      }
    },

  updateTextItem: (textValue: TextValue): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateTextItem(textValue));
    },

  updateTextItemDescription: (
    id: number, textId: number, textSet: number
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {
      if (!isDefinedIncludingZero(textId) || !isDefinedIncludingZero(textSet)) {
        dispatch(new UpdateTextItemDescription(id, undefined));
        return;
      }

      try {

        const text = await api.loadTextDescription(textId, textSet);
        dispatch(new UpdateTextItemDescription(id, text?.d));

      } catch (err) {
        dispatch(new UpdateTextItemDescription(id, undefined));
        dispatch(new UpdateTextsValidation(id, complexValidationFromServer(err?.validationErrors)));
      }
    },

  updateTextsValidation: (
    texts: ValidationMessage[],
    textValue: TextValue[]
  ): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      const textsValidation = texts?.map(item => {
        return textValue.filter(value =>
          value.textId === item?.parameters?.textId
          && value.textSet?.value === item?.parameters?.textSet
        );
      }).reduce((obj, item) => obj.concat(item), []);

      textsValidation?.forEach(text => {
        dispatch(new UpdateTextsValidation(
          text.id,
          complexValidationFromServer({ textDescription: texts } as ServerValidationErrors))
        );
      });
    },

  removeText: (id: number): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new RemoveText(id));
    },

  removeAllTexts: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new RemoveAllTexts());
      dispatch(new ResetAllTextsValidation());
    },

  addText: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new AddText());
    },

  resetTextValidation: (id: number, key?: keyof TextValue): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch: any, getState: any) => {
      dispatch(new ResetTextValidation(id, key));
    },

  resetTextsValidation: (id: number): ActionDispatching<AnyAction, ApplicationState> => (dispatch: any, getState: any) => {
    dispatch(new ResetTextsValidation(id));
  },

  updateMetadataItem: (metadataValue: MetadataValue): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateMetadataItem(metadataValue));
    },

  updateMetadataValidation: (
    id: number,
    validationProps: { [propertyName: string]: ValidationProps },
    validationMessage: string
  ): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ErrorNotification(validationMessage));
      dispatch(new UpdateMetadataValidation(id, validationProps));
    },

  removeMetadata: (id: number): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new RemoveMetadata(id));
    },

  removeAllMetadata: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new RemoveAllMetadata());
      dispatch(new ResetAllMetadataValidation());
    },

  addMetadata: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new AddMetadata());
    },

  resetMetadataValidation: (id: number, key?: keyof MetadataValue):
    ActionDispatching<AnyAction, ApplicationState> => (dispatch: any, getState: any) => {
      dispatch(new ResetMetadataValidation(id, key));
    },

  clearForm: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ClearForm());
    },

  updateItem: (item: Partial<CompanyReportItem>): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateItem(item));
    },

  changeLanguage: (languageId: number, text: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ChangeLanguage(languageId, text));
    },

  copyLanguageToAll: (languageId: number, successMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new CopyLanguageToAll(languageId));
      dispatch(new SuccessNotification(successMessage));
    },

  updateValidation: (validation: CompanyReportItemValidation): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateValidation(validation));
    },

  resetValidation: (key: string): ActionDispatching<AnyAction, ApplicationState> => (dispatch: any, getState: any) => {
    dispatch(new ResetValidation(key));
  },

  displayError: (errorMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ErrorNotification(errorMessage));
    }
};
